import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { environment } from '../../../environments/environment';

export interface GeneralLedgerListItem {
  generalLedgerId: string;
  generalLedger_name: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value: string;
}

export interface GeneralLedger {
  generalLedgerId: string;
  generalLedger_name: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value: string;
}

export interface GeneralLedgerReference {
  tablename: string;
  count: number;
}

interface GeneralLedgerApiModel {
  generalLedger_ID: string;
  generalLedger_Name: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value?: string | null;
}

interface AddGeneralLedgerResponse {
  data?: string;
  generalLedger_ID?: string;
  generalLedgerId?: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class GeneralledgerService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/GeneralLedger';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all general ledgers for the current context.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getByContext(): Observable<GeneralLedgerListItem[]> {
    return this.http
      .get<GeneralLedgerApiModel[] | null>(this.apiUrl + '/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiModelToListItem(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Removes a general ledger by its id.
   */
  removeGeneralLedger(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Returns the references that still point to a general ledger and would block
   * its deletion. Returns an empty array when the API responds with 204 No
   * Content, meaning the ledger can be deleted safely.
   */
  getReferenceCountForDeleteCheck(
    id: string,
    language = 'nl',
  ): Observable<GeneralLedgerReference[]> {
    return this.http
      .get<GeneralLedgerReference[] | null>(
        `${this.apiUrl}/GetReferenceCountForDeleteCheck/${id}?language=${language}`,
        this.httpOptions,
      )
      .pipe(
        map((data) => data ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns a single general ledger by its id.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns null when the API responds with 404 or 204 No Content.
   */
  getById(id: string): Observable<GeneralLedger | null> {
    return this.http
      .get<GeneralLedgerApiModel | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((item) => (item ? this.mapApiModelToModel(item) : null)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new general ledger. The id is always forced to the empty guid so
   * the backend can generate a new one.
   */
  addGeneralLedger(generalLedger: GeneralLedger): Observable<GeneralLedger> {
    const payload = this.mapModelToApiPayload({
      ...generalLedger,
      generalLedgerId: EMPTY_GUID,
    });

    return this.http
      .post<AddGeneralLedgerResponse | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => ({
          ...generalLedger,
          generalLedgerId: this.getCreatedGeneralLedgerId(response),
        })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Updates an existing general ledger.
   */
  updateGeneralLedger(id: string, generalLedger: GeneralLedger): Observable<GeneralLedger> {
    const payload = this.mapModelToApiPayload({
      ...generalLedger,
      generalLedgerId: id,
    });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...generalLedger, generalLedgerId: id })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  private mapApiModelToListItem(apiModel: GeneralLedgerApiModel): GeneralLedgerListItem {
    return {
      generalLedgerId: apiModel.generalLedger_ID,
      generalLedger_name: apiModel.generalLedger_Name,
      startDate: apiModel.startDate,
      endDate: apiModel.endDate,
      memo_Value: apiModel.memo_Value ?? '',
    };
  }

  private mapApiModelToModel(apiModel: GeneralLedgerApiModel): GeneralLedger {
    return {
      generalLedgerId: apiModel.generalLedger_ID,
      generalLedger_name: apiModel.generalLedger_Name,
      startDate: apiModel.startDate,
      endDate: apiModel.endDate,
      memo_Value: apiModel.memo_Value ?? '',
    };
  }

  private mapModelToApiPayload(model: GeneralLedger): GeneralLedgerApiModel {
    return {
      generalLedger_ID: model.generalLedgerId,
      generalLedger_Name: model.generalLedger_name,
      startDate: this.formatApiDate(model.startDate),
      endDate: this.formatApiDate(model.endDate),
      memo_Value: model.memo_Value ?? null,
    };
  }

  private getCreatedGeneralLedgerId(response: AddGeneralLedgerResponse | null): string {
    return response?.data ?? response?.generalLedger_ID ?? response?.generalLedgerId ?? EMPTY_GUID;
  }

  private formatApiDate(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
